<template>
  <div v-if="total > 0" class="pag">
    <div class="pag__info">
      <span class="pag__range">{{ from }}–{{ to }}</span>
      of <strong>{{ total }}</strong>
    </div>

    <div class="pag__controls">
      <button
        type="button"
        class="pag__page-btn"
        :disabled="currentPage <= 1"
        aria-label="Previous page"
        @click="goToPage(currentPage - 1)"
      >
        <i class="fa-regular fa-chevron-left"></i>
      </button>

      <span class="pag__page">
        Page <strong>{{ currentPage }}</strong> of {{ lastPage }}
      </span>

      <button
        type="button"
        class="pag__page-btn"
        :disabled="currentPage >= lastPage"
        aria-label="Next page"
        @click="goToPage(currentPage + 1)"
      >
        <i class="fa-regular fa-chevron-right"></i>
      </button>
    </div>

    <div class="pag__perpage">
      <app-select
        :value="perPage"
        :options="perPageSelectOptions"
        size="sm"
        @input="onPerPageInput"
      />
    </div>
  </div>
</template>

<script>
/**
 * Compact pagination footer — mirrors the search-attendees table footer:
 * range / total on the left, prev / "Page X of Y" / next in the center,
 * per-page <app-select> on the right.
 *
 * Controlled component — the parent owns `currentPage` / `perPage` state and
 * supplies fresh data on each emitted event.
 */
export default {
  name: 'AppPagination',

  props: {
    currentPage:    { type: Number, required: true },
    lastPage:       { type: Number, required: true },
    total:          { type: Number, required: true },
    from:           { type: Number, default: 0 },
    to:             { type: Number, default: 0 },
    perPage:        { type: Number, default: 20 },
    // Accept either raw numbers (`[10, 20, 50]` → auto-label) or full
    // `{label, value}` objects when the caller needs custom labels.
    perPageOptions: { type: Array,  default: () => [10, 20, 50, 100] },
  },

  emits: ['change', 'per-page-change'],

  computed: {
    perPageSelectOptions() {
      return this.perPageOptions.map((opt) =>
        typeof opt === 'object' && opt !== null
          ? opt
          : { label: `${opt} per page`, value: opt },
      )
    },
  },

  methods: {
    goToPage(p) {
      if (p < 1 || p > this.lastPage || p === this.currentPage) return
      this.$emit('change', p)
    },

    onPerPageInput(value) {
      const next = Number(value) || 20
      if (next === this.perPage) return
      this.$emit('per-page-change', next)
    },
  },
}
</script>

<style lang="scss" scoped>
// Self-contained tokens — mirror the attendees-search aesthetic so the
// pagination footer looks identical across the admin dashboard.
$pag-text:        #2b3a4a;
$pag-text-muted:  #6c7a89;
$pag-bg-surface:  #ffffff;
$pag-bg-soft:     #f6f8fa;
$pag-border:      rgba(0, 0, 0, 0.08);
$pag-blue-focus:  #4392d4;
$pag-radius-md:   8px;
$pag-duration:    140ms;
$pag-ease:        cubic-bezier(0.4, 0, 0.2, 1);

.pag {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 12px;
  padding: 4px 4px 0;
  flex-wrap: wrap;
  font-size: 12px;
  color: $pag-text-muted;

  &__info {
    flex: 1;

    strong { color: $pag-text; font-weight: 600; }
  }

  &__range { color: $pag-text; font-weight: 600; }

  &__controls {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__page {
    font-size: 12px;
    color: $pag-text-muted;
    min-width: 96px;
    text-align: center;

    strong { color: $pag-text; font-weight: 600; }
  }

  &__page-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: $pag-bg-surface;
    border: 0.5px solid $pag-border;
    border-radius: $pag-radius-md;
    color: $pag-text-muted;
    cursor: pointer;
    font-family: inherit;
    transition: background $pag-duration $pag-ease, color $pag-duration $pag-ease, border-color $pag-duration $pag-ease;

    &:hover:not(:disabled) {
      background: $pag-bg-soft;
      color: $pag-text;
      border-color: rgba(0, 0, 0, 0.16);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &:focus-visible {
      outline: 2px solid $pag-blue-focus;
      outline-offset: 1px;
    }

    i { font-size: 10px; }
  }

  &__perpage {
    width: 140px;
  }
}

@media (max-width: 700px) {
  .pag {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .pag__info { text-align: center; }
  .pag__controls { justify-content: center; }
  .pag__perpage { width: 100%; }
}
</style>
